import { useEffect, useRef } from 'react';
import { useMacroStore } from '@/store/macroStore';
import { useChartStore } from '@/store/chartStore';
import { api } from '@/services/api';

/**
 * Fetches macro series metadata once on mount.
 * Fetches time-series data for each newly pinned series.
 * Computes Pearson correlation between macro series and symbol close prices.
 */
export function useMacroData() {
  const pinnedSeries = useMacroStore((s) => s.pinnedSeries);
  const seriesData   = useMacroStore((s) => s.seriesData);
  const metaLoaded   = useMacroStore((s) => s.metaLoaded);
  const metaLoading  = useMacroStore((s) => s.metaLoading);
  const loadingIds   = useMacroStore((s) => s.loadingIds);
  const setSeriesMeta   = useMacroStore((s) => s.setSeriesMeta);
  const setMetaLoading  = useMacroStore((s) => s.setMetaLoading);
  const setSeriesData   = useMacroStore((s) => s.setSeriesData);
  const setLoadingId    = useMacroStore((s) => s.setLoadingId);
  const setCorrelation  = useMacroStore((s) => s.setCorrelation);

  const symbol    = useChartStore((s) => s.symbol);
  const timeframe = useChartStore((s) => s.timeframe);

  // Fetch metadata once
  useEffect(() => {
    if (metaLoaded || metaLoading) return;
    setMetaLoading(true);
    api.macroSeries()
      .then((res) => setSeriesMeta(res.series))
      .catch(console.error)
      .finally(() => setMetaLoading(false));
  }, [metaLoaded, metaLoading, setSeriesMeta, setMetaLoading]);

  // Track which IDs we've already kicked off a fetch for this render cycle
  const fetchedRef = useRef<Set<string>>(new Set());

  // Fetch data for any pinned series that we don't have yet
  useEffect(() => {
    for (const { seriesId } of pinnedSeries) {
      if (seriesData[seriesId] || loadingIds.has(seriesId) || fetchedRef.current.has(seriesId)) {
        continue;
      }
      fetchedRef.current.add(seriesId);
      setLoadingId(seriesId, true);
      api.macroData([seriesId], '2015-01-01')
        .then((res) => {
          const points = res.data[seriesId] ?? [];
          setSeriesData(seriesId, points);
        })
        .catch(console.error)
        .finally(() => setLoadingId(seriesId, false));
    }
  }, [pinnedSeries, seriesData, loadingIds, setSeriesData, setLoadingId]);

  // Re-compute correlations when symbol, timeframe, or series data changes
  useEffect(() => {
    if (pinnedSeries.length === 0) return;

    let cancelled = false;
    api.ohlcv(symbol, timeframe, 500)
      .then((res) => {
        if (cancelled) return;
        const bars = res.data;
        const closeBars = bars.map((b) => ({ time: b.time, close: b.close }));

        for (const { seriesId } of pinnedSeries) {
          const macroPoints = useMacroStore.getState().seriesData[seriesId];
          if (!macroPoints || macroPoints.length < 10) {
            setCorrelation(seriesId, null);
            continue;
          }
          const rho = computePearsonCorrelation(macroPoints, closeBars);
          setCorrelation(seriesId, rho);
        }
      })
      .catch(console.error);

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeframe, pinnedSeries.map((s) => s.seriesId + (seriesData[s.seriesId]?.length ?? 0)).join(','), setCorrelation]);
}

/**
 * Computes the Pearson correlation coefficient between a macro series
 * and a set of OHLCV close prices.
 *
 * Macro data is monthly; close prices are daily. We step-interpolate the
 * macro series (carry-forward last known value) to align with each bar date.
 *
 * @param macroPoints  Sorted ascending array of {date, value} points
 * @param closeBars    Array of {time (unix seconds), close} bars, oldest first
 * @returns Pearson r in [-1, 1], or null if insufficient overlapping data
 */
export function computePearsonCorrelation(
  macroPoints: Array<{ date: string; value: number }>,
  closeBars: Array<{ time: number; close: number }>,
): number | null {
  if (macroPoints.length < 2 || closeBars.length < 2) return null;

  // Build step-interpolated macro values aligned to each bar's date
  const pairs: Array<{ macro: number; close: number }> = [];
  let macroIdx = 0;

  for (const bar of closeBars) {
    const barDate = new Date(bar.time * 1000).toISOString().slice(0, 10);

    // Advance macroIdx to the last point whose date <= barDate
    while (
      macroIdx + 1 < macroPoints.length &&
      macroPoints[macroIdx + 1].date <= barDate
    ) {
      macroIdx++;
    }

    // Only include bars that fall on or after the first macro data point
    if (macroPoints[macroIdx].date > barDate) continue;

    pairs.push({ macro: macroPoints[macroIdx].value, close: bar.close });
  }

  if (pairs.length < 10) return null; // need at least 10 paired observations

  const n = pairs.length;
  const sumX  = pairs.reduce((s, p) => s + p.macro, 0);
  const sumY  = pairs.reduce((s, p) => s + p.close, 0);
  const sumXY = pairs.reduce((s, p) => s + p.macro * p.close, 0);
  const sumX2 = pairs.reduce((s, p) => s + p.macro * p.macro, 0);
  const sumY2 = pairs.reduce((s, p) => s + p.close * p.close, 0);

  const num = n * sumXY - sumX * sumY;
  const den = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

  if (den === 0) return null;
  return Math.max(-1, Math.min(1, num / den));
}
